import { AjaxRequest, AjaxResponse, ajax } from 'rxjs/ajax'
import { Subject, fromEvent, merge, of } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'
import { WrongParamsError } from 'models/helpers'

export class ApiClient {
  // API Requests and error handling
  apiHandlers = new Map<number, ((response: AjaxResponse) => AjaxResponse)[]>()
  unsynced$ = new Subject<number>()
  online$ = merge(of(null), fromEvent(window, 'offline'), fromEvent(window, 'online'))
    .pipe(map(() => navigator.onLine), shareReplay())

  stats: {
    requests: number
    errors: number
  } = {
    requests: 0,
    errors: 0,
  }

  constructor(public baseUrl: string) {}

  /**
   * Makes an API call, applying any matching middleware handlers to the response
   * @param method an HTTP method
   * @param url the url to call
   * @param config additional request config, like a body or headers
   */
  protected async REQUEST(method: string, url: string, config?: Partial<AjaxRequest>, decorate?: any): Promise<AjaxResponse> {
    if (decorate && Object.keys(decorate).length) { url = `${url}?${new URLSearchParams(decorate).toString()}` }
    if (/(\[|%5B)(.*)Object(.*)(\]|%5D)/.test(url)) { throw new WrongParamsError('Can not parse nested params yet. Ignoring request', url) }
    this.stats.requests++
    return this.MAKE_API_CALL(method, url, config)
      .then(response => this.processHandlers(response))
      .catch(err => this.processHandlers(err))
      .then(response => {
        if (response.status >= 400) {
          this.stats.errors++
          throw response
        }
        return response
      })
  }

  async GET(url: string, config?: Partial<AjaxRequest>, decorate?: any) { return this.REQUEST('GET', url, config, decorate) }
  async POST(url: string, config?: Partial<AjaxRequest>, decorate?: any) { return this.REQUEST('POST', url, config, decorate) }
  async PUT(url: string, config?: Partial<AjaxRequest>, decorate?: any) { return this.REQUEST('PUT', url, config, decorate) }
  async DELETE(url: string, config?: Partial<AjaxRequest>, decorate?: any) { return this.REQUEST('DELETE', url, config, decorate) }

  /**
   * Store a response handler
   * @param code The HTTP status code to apply this handler to. Use 1 to apply it to ALL other codes (after the specific handler)
   * @param handler a function to perform on the response, can modify it. Throw an error to break the chain.
   */
  registerApiHandler(code: number, handler: (response: AjaxResponse) => AjaxResponse) {
    const handlers = this.apiHandlers.get(code) || []
    handlers.push(handler)
    this.apiHandlers.set(code, handlers)
  }

  /**
   * Calls any side effects that might match a status code for a given response
   * @param response an HTTP response
   */
  private processHandlers(response: AjaxResponse) {
    const { status } = response
    const statusHandlers = this.apiHandlers.get(status)
    if (statusHandlers && statusHandlers.length) {
      for (const handler of statusHandlers) { response = handler(response) }
    }

    const globalHandlers = this.apiHandlers.get(1)
    if (globalHandlers && globalHandlers.length) {
      for (const handler of globalHandlers) { response = handler(response) }
    }

    return response
  }

  /**
   * Seperate to be easily mockable. Makes the actual HTTP call
   * @param method 
   * @param url 
   * @param config 
   */
  private async MAKE_API_CALL(method: string, url: string, config?: Partial<AjaxRequest>) {
    return ajax({
      method,
      url: `${this.baseUrl}${url}`, // TODO turn into environment variable
      withCredentials: true,
      crossDomain: true,
      headers: method === 'GET'
        ? {}
        : {
          'Content-Type': 'application/json',
        },
      ...config,
    }).toPromise()
  }
}
